import { TemplateBuilder } from '#pplx/frontend/framework/templates/templateComponents.js';
import { templateEngineStore } from '#pplx/frontend/framework/templates/templateEngineStore.js';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
/**
 * Slot function → owning template name, resolved by useSlotActive at runtime.
 * Both flavors register: the layout `Slot` (per Renderer) and `Template.Slot`.
 */
export const slotTokenTemplateNames = new WeakMap();
/**
 * Builder class for defining type-safe templates with named slots.
 *
 * Uses generic accumulation pattern (like tRPC) where each .slot() call
 * extends the TSlots type parameter with the new slot's props.
 */
/**
 * Reactive active-state for a template's slots: `true` while at least one
 * `<Template.Slot slot="<name>">` is mounted for that name — not whether its
 * children render anything. To make a region inactive, conditionally render
 * the Slot element itself, not its contents.
 *
 * The first argument is any Slot function of the target template (the layout's
 * `Slot` argument or `Template.Slot`); it carries both the slots typing and the
 * template identity, so the binding stays unambiguous under nested templates.
 * Pass the framework-provided function through unchanged — a hand-wrapped
 * function is not a valid token and throws.
 * Returns booleans keyed by exactly the requested names and re-renders only
 * when one of them flips. Call it from the smallest component that owns the
 * gated region so slot-active re-renders stay local.
 */
export function useSlotActive(Slot, ...names) {
    const templateName = slotTokenTemplateNames.get(Slot);
    if (templateName === undefined) {
        throw new Error('useSlotActive: unknown Slot token — pass the layout `Slot` argument or a `Template.Slot`.');
    }
    return useStore(templateEngineStore, useShallow((s) => {
        const instance = s.templates.get(templateName);
        return Object.fromEntries(names.map((name) => [name, (instance?.activeSlots.get(name) ?? 0) > 0]));
    }));
}
/**
 * Create a type-safe template with named slots.
 *
 * @example
 * ```tsx
 * type PageSlots = { header: { variant: 'primary' | 'secondary' }; footer: NoSlotProps };
 *
 * const PageLayout = template<{ className?: string }>('PageLayout')
 *   .slot<{ variant: 'primary' | 'secondary' }, 'header'>('header', HeaderRoot)
 *   .slot<NoSlotProps, 'footer'>('footer', FooterRoot)
 *   .define(({ Slot, props }) => (
 *     <div className={props.className}>
 *       <Slot slot="header" />
 *       <FooterGate Slot={Slot} />
 *     </div>
 *   ));
 *
 * // Gate a layout region on slot activity; the layout's Slot is the token.
 * function FooterGate({ Slot }: { Slot: LayoutSlot<PageSlots> }) {
 *   const { footer } = useSlotActive(Slot, 'footer');
 *   return footer ? <Slot slot="footer" /> : null;
 * }
 *
 * // Usage
 * function MyPage() {
 *   const { set } = PageLayout.useSlotProps();
 *   useEffect(() => {
 *     set({ header: { variant: 'primary' } });
 *   }, [set]);
 *
 *   return (
 *     <>
 *       <PageLayout.Renderer className="my-page" />
 *       <PageLayout.Slot slot="header">
 *         <h1>Title</h1>
 *       </PageLayout.Slot>
 *     </>
 *   );
 * }
 * ```
 */
export function template(name) {
    return new TemplateBuilder(name);
}
//# sourceMappingURL=template.js.map